import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './App.css';
import { useNavigate } from 'react-router-dom';
import { awardXp } from './utils/xp';
import { userService, USER_ID_KEY } from './services/userService';

interface Pensum {
  id: string;
  name?: string;
  imagePath?: string;
  description?: string;
  type?: string;
  beltColor: string;
}

interface QuizPageProps {
    selectedBelt: string;
    onNextMode?: () => void;
    onScore?: (score: number) => void;
}

function PensumQuiz({ selectedBelt, onNextMode, onScore }: QuizPageProps) {
      console.log('Quiz component rendered', onNextMode, onScore);
  const navigate = useNavigate();
  const [items, setItems] = useState<Pensum[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [correctAnswer, setCorrectAnswer] = useState<string | null>(null);
  const [userAnswers, setUserAnswers] = useState<{ question: string; selected: string; correct: string }[]>([]);

  // Reset all state when items are reloaded (e.g., on mode switch)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const username = process.env.REACT_APP_API_USERNAME;
        const password = process.env.REACT_APP_API_PASSWORD;
        const token = btoa(`${username}:${password}`);
        const response = await axios.get<Pensum[]>(`${process.env.REACT_APP_API_URL}/Pensum`, {
          headers: { Authorization: `Basic ${token}` },
        });
        let filteredItems = response.data.filter(item => item.beltColor === selectedBelt);
        // Shuffle the filtered items so the first question is random each time
        filteredItems = filteredItems.sort(() => 0.5 - Math.random());
        setItems(filteredItems);
        setCurrentQuestion(0);
        setScore(0);
        setCompleted(false);
        setUserAnswers([]);
        if (filteredItems.length > 0) {
          loadQuestion(filteredItems, 0);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBelt]);

  const loadQuestion = (items: Pensum[], questionIndex: number) => {
    const currentItem = items[questionIndex];
    const correctOption = currentItem.name ?? 'Unknown';
    // Pick 4 random wrong answers from the REMAINING items (excluding current)
    const wrongOptions = items
      .filter((item, idx) => idx !== questionIndex && item.name !== undefined && item.name !== correctOption)
      .sort(() => 0.5 - Math.random())
      .slice(0, 4)
      .map((item) => item.name ?? 'Unknown');
    // Combine correct option and wrong options, then shuffle again
    const shuffledOptions = [...wrongOptions, correctOption].sort(() => 0.5 - Math.random());
    setCorrectAnswer(correctOption);
    setOptions(shuffledOptions);
  };

  const handleAnswer = (selectedOption: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (selectedOption === correctAnswer) {
      setScore(score + 1);
      if (onScore) onScore(1);
      awardXp();
      const userId = localStorage.getItem(USER_ID_KEY);
      if (userId) {
        userService.incrementProgress(userId, items[currentQuestion].id).catch(() => {});
      }
    }

    // Record the user's answer
    setUserAnswers((prevAnswers) => [
        ...prevAnswers,
        {
            question: items[currentQuestion].name ?? 'Unknown',
            selected: selectedOption,
            correct: correctAnswer ?? 'Unknown',
        },
    ]);

    // Remove focus from the button after clicking
    event.currentTarget.blur();

    if (onNextMode) onNextMode();

    const nextQuestion = currentQuestion + 1;
    if (nextQuestion < items.length) {
      setCurrentQuestion(nextQuestion);
      loadQuestion(items, nextQuestion);
    } else {
      if (!onNextMode) awardXp(30);
      setCompleted(true);
    }
  };

  const calculateResult = () => {
    const percentage = (score / items.length) * 100;
    return percentage > 75 ? 'You Passed!' : 'You Failed.';
  };

  if (completed) {
    const wrongAnswers = userAnswers.filter((answer) => answer.selected !== answer.correct);

    return (
        <div className={`quiz-result ${calculateResult() === 'You Passed!' ? 'passed' : 'failed'}`}>
          {!onNextMode && <button className="close-button" onClick={() => navigate('/')}>&times;</button>}
            <h2 className="quiz-result-title">Quiz Completed</h2>
            <p className="quiz-score">Your score: {score}/{items.length}</p>
            <p className="quiz-result-message">{calculateResult()}</p>
            {calculateResult() === 'You Passed!' && (
                <div className="confetti-container">
                    <p>🎉 Congratulations! 🎉</p>
                </div>
            )}
            {wrongAnswers.length > 0 && (
                <div className="wrong-answers">
                    <h3>Wrongly Answered Questions:</h3>
                    <ul>
                        {wrongAnswers.map((answer, index) => (
                            <span key={index}>
                                <strong>Your Answer:</strong> {answer.selected} <br />
                                <strong>Correct Answer:</strong> {answer.correct} <br />
                                <hr />
                            </span>
                        ))}
                    </ul>
                </div>
            )}
        </div>       
    );
  }

  if (items.length === 0) {
    return <p>Intet fundet.</p>;
  }

  const currentItem = items[currentQuestion];

  return (
    <div className="quiz-container">
        {!onNextMode && <button className="close-button" onClick={() => navigate('/')}>&times;</button>}
        <div className="quiz-progress-bar">
          <div className="quiz-progress-fill" style={{ width: `${(currentQuestion / items.length) * 100}%` }} />
        </div>
        <h2 className="quiz-title">Vælg det rette japanske udtryk</h2>
        {currentItem.imagePath ? (
            <img src={'../img/' + currentItem.imagePath} alt="Quiz Item" className="quiz-image" />
        ) : (
            <p className="quiz-description">{currentItem.description ?? 'No description available'}</p>
        )}
        <div className="options">
            {options.map((option, index) => (
            <button key={index} onClick={(event) => handleAnswer(option, event)} className="quiz-option">
                {option}
            </button>
            ))}
        </div>
    </div>
  );
}

export default PensumQuiz;
